import { calculatePlanPrice } from '@/apis/product';
import { createHomeProposal, retrieveHomeProposal } from '@/apis/proposal';
import { Gender, Risk } from '@/enums';
import { DeclarationItem } from '@/types/PlanSummary/DeclarationItem';
import { PlanSummaryState } from '@/types/PlanSummary/PlanSummaryState';
import {
  MyRumahQuotationData,
  QuotationData,
} from '@/types/PlanSummary/QuotationData';
import { HomeRetrieveProposalResponse } from '@/types/Proposal';
import { CalculatePlanPriceData } from '@/types/Proposal/CalculatePlanPriceData';
import { ApiResponseWrapper } from '@/types/Wrapper';
import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import { format, parseISO } from 'date-fns';

export const initialState: PlanSummaryState = {
  declaration: false,
  marketingConsent: false,
  payMode: '',
  quotationDetail: {
    personalDetails: {
      name: '',
      idType: 0,
      idNumber: '',
      dateOfBirth: '',
      gender: Gender.M,
      secondaryIdType: 0,
      secondaryIdNumber: '',
      alternateIdType: 0,
      ethnicity: 0,
      mobileNo: '',
      email: '',
    },
    insuredDetails: [],
    propertyDetails: {
      address: {
        addressLine1: '',
        addressLine2: '',
        city: '',
        state: '',
        country: '',
        postcode: '',
      },
    },
    ownership: 0,
    planType: 0,
    noOfStorey: 0,
    buildingSumInsured: 0,
    contentSumInsured: 0,
    unoccupied: false,
    lossOrDamage: false,
    floodRisk: Risk.LOW,
    startDate: '',
  },
  declarationList: [],
  addOn: [],
  planPrice: undefined,
  encodedOrderId: '',
  loading: false,
  error: false,
  readyForPayment: false,
};

const planSummarySlice = createSlice({
  name: 'planSummary',
  initialState,
  reducers: {
    resetPlanSummaryState: () => {
      return initialState;
    },
    updateQuotationDetail: (state, action: PayloadAction<QuotationData>) => {
      state.quotationDetail = action.payload;
    },
    updateMyRumahQuotationDetail: (
      state,
      action: PayloadAction<MyRumahQuotationData>,
    ) => {
      const data: QuotationData = {
        ...action.payload,
        personalDetails: state.quotationDetail.personalDetails,
        insuredDetails: action.payload.insuredDetails,
        propertyDetails: {
          address: {
            addressLine1: '',
            addressLine2: '',
            city: '',
            state: '',
            country: '',
            postcode: action.payload.postcode,
          },
        },
        noOfStorey: action.payload.noOfStorey ?? 0,
        planType: 1,
        buildingSumInsured: 0,
        contentSumInsured: 0,
        unoccupied: false,
        lossOrDamage: false,
        floodRisk: action.payload.floodRisk,
      };
      state.quotationDetail = data;
    },
    updateAddOn: (state, action: PayloadAction<string>) => {
      if (state.addOn) {
        state.addOn.push(action.payload);
      } else {
        state.addOn = [action.payload];
      }
    },
    removeAddon: (state, action: PayloadAction<string>) => {
      if (state.addOn) {
        state.addOn = state.addOn.filter((element) => {
          return element !== action.payload;
        });
      } else {
        state.addOn = [];
      }
    },
    addDeclarationItem: (state, action: PayloadAction<DeclarationItem>) => {
      if (state.declarationList) {
        state.declarationList.push(action.payload);
      } else {
        state.declarationList = [action.payload];
      }
    },
    updateDeclarationItem: (
      state,
      action: PayloadAction<DeclarationItem[]>,
    ) => {
      state.declarationList = action.payload;
    },
    removeDeclarationItem: (state, action: PayloadAction<DeclarationItem>) => {
      state.declarationList = state.declarationList.filter(
        (item) =>
          item.declarationType !== action.payload.declarationType ||
          item.description !== action.payload.description ||
          item.value !== action.payload.value,
      );
    },
    updateDeclaration: (state, action: PayloadAction<boolean>) => {
      state.declaration = action.payload;
    },
    updateMarketingConsent: (state, action: PayloadAction<boolean>) => {
      state.marketingConsent = action.payload;
    },
    updatePayMode: (state, action: PayloadAction<string>) => {
      state.payMode = action.payload;
    },
    mapProposalDataToPlanSummary: (
      state,
      action: PayloadAction<HomeRetrieveProposalResponse>,
    ) => {
      const data = action.payload;
      state.readyForPayment = true;
      state.addOn = data.addOnList.flatMap((x) => x.code);
      state.declarationList = data.home.declaration;
      state.quotationDetail = {
        personalDetails: {
          name: data.policyOwner.name,
          formerName: data.policyOwner.formerName,
          aliasName: data.policyOwner.aliasName,
          idType: Number(data.policyOwner.identityType),
          idNumber: data.policyOwner.identityNumber,
          idNoExpiryDate: data.policyOwner.idNoExpiryDate ?? '',
          secondaryIdType: Number(data.policyOwner.secondaryIdentityType),
          secondaryIdNumber: data.policyOwner.secondaryIdentityNumber ?? '',
          secondaryIdNoExpiryDate:
            data.policyOwner.secondaryIdNoExpiryDate ?? '',
          alternateIdType: data.policyOwner.alternateIdType,
          alternateIdNo: data.policyOwner.alternateIdNo ?? '',
          alternateIdNoExpiryDate:
            data.policyOwner.alternateIdNoExpiryDate ?? '',
          nationality: data.policyOwner.nationality,
          dualNationality: data.policyOwner.dualNationality,
          dateOfBirth: format(
            parseISO(data.policyOwner.birthDate),
            'dd/MM/yyyy',
          ),
          gender: data.policyOwner.gender as Gender,
          ethnicity: data.policyOwner.ethnicity,
          mobileNo: data.policyOwner.mobile ?? '',
          email: data.policyOwner.email ?? '',
        },
        insuredDetails: (data.insured ?? []).map((insured: any) => ({
          name: insured.name,
          formerName: insured.formerName ?? '',
          aliasName: insured.aliasName ?? '',
          idType: Number(insured.identityType),
          idNumber: insured.identityNumber,
          idNoExpiryDate: insured.idNoExpiryDate ?? '',
          secondaryIdType: insured.secondaryIdentityType
            ? Number(insured.secondaryIdentityType)
            : undefined,
          secondaryIdNumber: insured.secondaryIdentityNumber ?? '',
          secondaryIdNoExpiryDate: insured.secondaryIdNoExpiryDate ?? '',
          alternateIdType: insured.alternateIdType,
          alternateIdNo: insured.alternateIdNo ?? '',
          alternateIdNoExpiryDate: insured.alternateIdNoExpiryDate ?? '',
          nationality: insured.nationality,
          dualNationality: insured.dualNationality,
          dateOfBirth: insured.birthDate
            ? format(parseISO(insured.birthDate), 'dd/MM/yyyy')
            : '',
          gender: insured.gender,
          ethnicity: insured.ethnicity,
          address: insured.address
            ? {
                addressLine1: insured.address.addressLine1,
                addressLine2: insured.address.addressLine2,
                postcode: insured.address.postcode,
                city: insured.address.city,
                state: insured.address.state,
                country: insured.address.country,
              }
            : undefined,
        })),
        propertyDetails: {
          startDate: format(parseISO(data.startDate), 'yyyy-MM-dd'),
          buildingType: data.home.buildingType,
          noOfStorey: data.home.noOfStorey,
          constructionType: data.home.constructionType,
          address: {
            addressLine1: data.home.address.address1,
            addressLine2: data.home.address.address2,
            postcode: data.home.address.postcode,
            city: data.home.address.city,
            state: data.home.address.state,
            country: data.home.address.country,
          },
        },
        ownership: data.home.ownership,
        buildingType: data.home.buildingType,
        constructionType: data.home.constructionType,
        noOfStorey: data.home.noOfStorey,
        planType: data.home.planType,
        buildingSumInsured: data.home.buildingSumInsured,
        contentSumInsured: data.home.contentSumInsured,
        startDate: format(parseISO(data.startDate), 'yyyy-MM-dd'),
        unoccupied: false,
        lossOrDamage: false,
        floodRisk: Risk.LOW,
      };

      state.planPrice = {
        discount:
          data.discountAmount !== 0
            ? [
                {
                  discountAmount: data.discountAmount,
                  discountRate: data.discountRate,
                },
              ]
            : ([] as unknown as CalculatePlanPriceData['discount']),
        serviceTaxRate: data.taxRate,
        serviceTaxAmount: data.taxAmount,
        stampDutyAmount: data.stampDuty,
        quotationNo: '',
        planPremium: data.planPremium,
        grossPremium: data.premium,
        netPremium: data.netPremium,
        totalPremium: data.totalPremium,
        totalPremiumBeforeDiscount: data.totalPremiumBeforeDiscount,
        status: !!data.status,
        endDate: format(parseISO(data.endDate), 'dd/MM/yyyy'),
        addOn: [],
        eligibleAddOn: [],
        declineCode: '',
        errorMessage: '',
        commission: 0,
        commissionRate: 0,
      };

      data.addOnList.forEach((x: any) => {
        const addOnData = {
          addOnId: x.addOnId,
          code: x.code,
          name: x.name,
          description: x.description,
          longDescription: '',
          addOnPremium: x.grossPremium,
          addOnRate: 0,
          isSelected: true,
        };
        state.planPrice?.addOn.push(addOnData);
      });
    },
  },
  extraReducers(builder) {
    builder
      .addCase(calculatePlanPrice.pending, (state) => {
        state.loading = true;
        state.error = false;
      })
      .addCase(calculatePlanPrice.fulfilled, (state, action) => {
        state.loading = true;
        const planPriceResponse = action.payload.data as CalculatePlanPriceData;
        state.planPrice = planPriceResponse ?? state.planPrice;
        state.loading = false;
        state.error = false;
      })
      .addCase(calculatePlanPrice.rejected, (state) => {
        state.loading = false;
        state.error = true;
      });

    builder.addCase(createHomeProposal.fulfilled, (state, action) => {
      if (action.payload?.succeeded) {
        state.encodedOrderId = action.payload.data;
      }
      state.loading = false;
      state.error = !action.payload?.succeeded;
    });

    builder
      .addCase(retrieveHomeProposal.pending, (state) => {
        state.loading = true;
        state.proposalData = {
          message: '',
        } as ApiResponseWrapper<HomeRetrieveProposalResponse>;
      })
      .addCase(retrieveHomeProposal.fulfilled, (state, action) => {
        state.loading = false;
        const proposalData =
          action.payload as ApiResponseWrapper<HomeRetrieveProposalResponse>;
        state.proposalData = proposalData;

        if (proposalData.succeeded)
          state.encodedOrderId = proposalData.data.encodedOrderId;
      })
      .addCase(retrieveHomeProposal.rejected, (state, action) => {
        state.loading = false;
        state.proposalData =
          action.payload as ApiResponseWrapper<HomeRetrieveProposalResponse>;
      });
  },
});

export const {
  resetPlanSummaryState,
  updateQuotationDetail,
  updateMyRumahQuotationDetail,
  updateAddOn,
  removeAddon,
  addDeclarationItem,
  updateDeclarationItem,
  removeDeclarationItem,
  updateDeclaration,
  updateMarketingConsent,
  updatePayMode,
  mapProposalDataToPlanSummary,
} = planSummarySlice.actions;

export default planSummarySlice.reducer;
